import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getApi } from "../../providers/AuthContext";
import { PapErrors } from "../FormElements";
import { Calendar, AlertTriangle, Loader2, Link2, Link2Off, CheckCircle2, Zap } from "lucide-react";

interface Integration {
  id: number;
  provider: string;
  status: string;
  connected: boolean;
  token_expired: boolean;
  scopes?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

const PROVIDER_CONFIG: Record<string, { name: string; description: string; disconnectedDescription: string; icon: React.ReactNode }> = {
  google_calendar: {
    name: "Google Calendar",
    description: "Sync events and manage your schedule from your profile cards.",
    disconnectedDescription: "Connect your Google Calendar to sync events and manage your schedule.",
    icon: <Calendar className="w-5 h-5" />,
  },
};

function providerLabel(provider: string): string {
  return PROVIDER_CONFIG[provider]?.name || provider.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const isMockMode = import.meta.env.VITE_MOCK_API === "true";

const Integrations: React.FC = () => {
  const api = getApi();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState<number | string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadIntegrations = async () => {
    try {
      const result = await api.get("/api/v1/integrations");
      setIntegrations(result.integrations || []);
    } catch {
      setErrors(["Failed to load integrations."]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  const connectIntegration = async (provider: string) => {
    setActionLoading(provider);
    setErrors([]);
    setSuccessMessage(null);
    try {
      if (provider === "google_calendar" && !isMockMode) {
        sessionStorage.setItem("preOAuthPath", window.location.pathname);
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `/api/v1/calendars/connect`;
        const token = api.token;
        if (token) {
          const tokenInput = document.createElement("input");
          tokenInput.type = "hidden";
          tokenInput.name = "access_token";
          tokenInput.value = token;
          form.appendChild(tokenInput);
        }
        document.body.appendChild(form);
        form.submit();
        return;
      }
      await api.post("/api/v1/integrations", { integration: { provider } });
      await loadIntegrations();
      setSuccessMessage(`${providerLabel(provider)} connected successfully.`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setErrors([`Failed to connect ${providerLabel(provider)}.`]);
    } finally {
      setActionLoading(null);
    }
  };

  const disconnectIntegration = async (integration: Integration) => {
    setActionLoading(integration.id);
    setErrors([]);
    setSuccessMessage(null);
    try {
      if (integration.provider === "google_calendar" && !isMockMode) {
        await api.delete("/api/v1/calendars/disconnect");
      } else {
        await api.delete(`/api/v1/integrations/${integration.id}`);
      }
      await loadIntegrations();
      setSuccessMessage(`${providerLabel(integration.provider)} disconnected.`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setErrors([`Failed to disconnect ${providerLabel(integration.provider)}.`]);
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("calendar_connected") === "true") {
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => loadIntegrations(), 1000);
    }
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && actionLoading !== null) {
        setTimeout(() => {
          loadIntegrations();
          setActionLoading(null);
        }, 1000);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [actionLoading]);

  const allProviders = ["google_calendar"];

  return (
    <div>
      <PapErrors errors={errors} />

      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-2.5 p-3.5 bg-success/[0.06] dark:bg-success/[0.1] border border-success/20 rounded-xl mb-4 text-sm text-success"
          >
            <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <span className="font-medium">{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
            <span className="text-xs text-muted-foreground">Loading integrations...</span>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {allProviders.map((provider) => {
            const config = PROVIDER_CONFIG[provider];
            const integration = integrations.find(
              (i) => i.provider === provider && i.status === "active"
            );
            const isConnected = !!integration;
            const isLoading = actionLoading === provider || actionLoading === integration?.id;

            return (
              <motion.div
                key={provider}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl border-2 transition-all duration-300 ${
                  isConnected
                    ? 'border-success/25 bg-success/[0.03] dark:bg-success/[0.06] shadow-soft-sm'
                    : 'border-border/60 bg-muted/20 dark:bg-muted/10 hover:border-border hover:shadow-soft-sm'
                }`}
              >
                <div className="p-4 sm:p-5 flex items-center gap-4">
                  {/* Provider icon */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    isConnected
                      ? 'bg-success/10 dark:bg-success/15 text-success shadow-soft-xs'
                      : 'bg-muted/60 dark:bg-muted/30 text-muted-foreground'
                  }`}>
                    {config?.icon || <Link2 className="w-5 h-5" />}
                  </div>

                  {/* Provider info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold m-0 text-foreground">{providerLabel(provider)}</h3>
                      {isConnected && (
                        <span className="inline-flex items-center gap-1.5 text-2xs font-semibold text-success bg-success/10 dark:bg-success/15 px-2 py-0.5 rounded-lg">
                          <Zap className="w-3 h-3" />
                          Connected
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 m-0 leading-relaxed">
                      {isConnected
                        ? config?.description || "Connected and syncing."
                        : config?.disconnectedDescription || `Connect your ${providerLabel(provider)} to get started.`}
                    </p>
                    {integration?.token_expired && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center gap-1.5 mt-2 text-xs text-destructive font-medium bg-destructive/[0.06] dark:bg-destructive/[0.1] px-2.5 py-1 rounded-lg w-fit"
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Token expired — please reconnect
                      </motion.div>
                    )}
                  </div>

                  {/* Action button */}
                  <div className="flex-shrink-0">
                    {isConnected ? (
                      <button
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl border border-destructive/25 text-destructive hover:bg-destructive/[0.06] dark:hover:bg-destructive/[0.1] transition-all disabled:opacity-50 active:scale-[0.97]"
                        onClick={() => disconnectIntegration(integration)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Link2Off className="w-3.5 h-3.5" />
                        )}
                        {isLoading ? "Disconnecting..." : "Disconnect"}
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary text-xs px-5 py-2"
                        onClick={() => connectIntegration(provider)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Link2 className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        {isLoading ? "Connecting..." : "Connect"}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {allProviders.length === 0 && (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-muted/60 dark:bg-muted/30 flex items-center justify-center mx-auto mb-3">
                <Link2Off className="w-6 h-6 text-muted-foreground/40" />
              </div>
              <h4 className="text-sm font-bold text-foreground mb-1">No integrations available</h4>
              <p className="text-xs text-muted-foreground">Check back later for new integrations.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Integrations;
